import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const API_BASE = "/api/repricing";
const CONFIRM_TEXT = "PUSH EBAY PRICES";

const money = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "";
};

const numberText = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString() : "";
};

const blockerLabel = (value) =>
  ({
    stale_ebay_data: "Stale eBay data",
    missing_ebay_sync: "Missing eBay sync",
    missing_ebay_offer_id: "Missing offer ID",
    missing_ebay_listing_id: "Missing listing ID",
    approved_price_below_floor: "Below floor",
    approved_price_above_guardrail_ceiling: "Above ceiling",
    approved_price_below_floor_manual_override: "Below floor override",
    approved_price_above_ceiling_manual_override: "Above ceiling override",
    low_current_margin: "Low current margin",
    low_current_profit: "Low current profit",
    missing_expected_margin: "Missing margin",
    missing_unit_cost: "Missing cost",
    safe_floor_blocks_discount: "Floor blocks discount",
    safe_floor_unavailable: "Floor unavailable",
    manual_override_below_guardrail_floor: "Manual below floor",
    manual_override_above_current_price_guardrail: "Manual above current",
    already_pushed: "Already pushed",
    blocked_recommendation_status: "Blocked",
    recommendation_has_data_quality_blockers: "Data blockers",
    listing_not_listed: "Not listed",
    ebay_sync_failed: "Sync failed",
    missing_approved_price: "Missing approved price",
    not_approved_for_push: "Not approved",
    repricing_recommendation_not_found: "Missing recommendation",
  }[value] || value || "Blocked");

const resultColor = (status, success) => {
  if (success || status === "success") return "success";
  if (status === "blocked") return "warning";
  if (status === "verification_mismatch" || status === "verification_failed") return "error";
  if (status === "failed" || status === "rejected") return "error";
  return "default";
};

const resultLabel = (row) =>
  ({
    success: row.price_verified ? "Verified" : "Success",
    verification_mismatch: "Mismatch",
    verification_failed: "Not verified",
    failed: "Failed",
    blocked: "Blocked",
    rejected: "Rejected",
  }[row.push_status] || row.push_status || "Result");

const idsFromModel = (model) => {
  if (Array.isArray(model)) return model.map(String);
  if (model?.ids instanceof Set) return Array.from(model.ids).map(String);
  return [];
};

export default function RepricingPushQueue() {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selection, setSelection] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [result, setResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmMode, setConfirmMode] = useState("selected");
  const [confirmText, setConfirmText] = useState("");

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/push-queue?limit=500`);
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || data.message || "Failed to load push queue");
      }
      setRows(Array.isArray(data.rows) ? data.rows : []);
      setSummary(data.summary || null);
      setSelection((current) => {
        const available = new Set((data.rows || []).map((row) => String(row.recommendation_id || row.id)));
        return current.filter((id) => available.has(String(id)));
      });
    } catch (e) {
      setRows([]);
      setSummary(null);
      setSelection([]);
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const selectedRows = useMemo(() => {
    const selected = new Set(selection.map(String));
    return rows.filter((row) => selected.has(String(row.recommendation_id || row.id)));
  }, [rows, selection]);

  const selectedEligibleCount = selectedRows.filter((row) => row.push_eligible).length;
  const firstEligibleRow = rows.find((row) => row.push_eligible);
  const testRow = selectedRows.find((row) => row.push_eligible) || firstEligibleRow;
  const confirmIds =
    confirmMode === "test"
      ? testRow
        ? [testRow.recommendation_id || testRow.id]
        : []
      : selectedRows.map((row) => row.recommendation_id || row.id);
  const confirmationReady = confirmText.trim() === CONFIRM_TEXT && confirmIds.length > 0;

  const openConfirm = (mode) => {
    setConfirmMode(mode);
    setConfirmText("");
    setError("");
    setNotice("");
    setConfirmOpen(true);
  };

  const pushRows = useCallback(async () => {
    if (!confirmationReady) return;
    setPushing(true);
    setError("");
    setNotice("");
    try {
      const res = await fetch(`${API_BASE}/push-queue/push`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recommendation_ids: confirmIds,
          approved: true,
          operator: "operator",
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        if (!data?.rows) throw new Error(data.error || data.message || "Price push failed");
      }
      setResult(data);
      const verifiedCount = data?.summary?.verified_count || data?.summary?.success_count || 0;
      const mismatchCount = data?.summary?.mismatch_count || 0;
      const verificationFailedCount = data?.summary?.verification_failed_count || 0;
      const otherFailedCount = Math.max(
        (data?.summary?.failed_count || 0) + (data?.summary?.blocked_count || 0) - mismatchCount - verificationFailedCount,
        0
      );
      setNotice(
        `Push finished: ${numberText(verifiedCount)} verified, ${numberText(mismatchCount)} mismatched, ${numberText(verificationFailedCount)} not verified, ${numberText(otherFailedCount)} failed or blocked.`
      );
      setConfirmOpen(false);
      setConfirmText("");
      await loadQueue();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setPushing(false);
    }
  }, [confirmIds, confirmationReady, loadQueue]);

  const columns = useMemo(
    () => [
      {
        field: "sku",
        headerName: "SKU",
        width: 120,
      },
      {
        field: "listing_title",
        headerName: "Listing",
        flex: 1,
        minWidth: 230,
        renderCell: (params) => params.row.listing_title || params.row.card_name || "",
      },
      {
        field: "push_eligible",
        headerName: "Ready",
        width: 105,
        renderCell: (params) => (
          <Chip
            size="small"
            color={params.value ? "success" : "warning"}
            label={params.value ? "Ready" : "Blocked"}
          />
        ),
      },
      {
        field: "approved_price",
        headerName: "Approved",
        width: 115,
        renderCell: (params) => money(params.value),
      },
      {
        field: "current_price",
        headerName: "Current",
        width: 105,
        renderCell: (params) => money(params.value),
      },
      {
        field: "ebay_current_price",
        headerName: "eBay",
        width: 100,
        renderCell: (params) => money(params.value),
      },
      {
        field: "push_blockers",
        headerName: "Blockers / warnings",
        flex: 1,
        minWidth: 260,
        sortable: false,
        renderCell: (params) => {
          const blockers = Array.isArray(params.value) ? params.value : [];
          const warnings = Array.isArray(params.row.push_warnings) ? params.row.push_warnings : [];
          const items = blockers.length ? blockers : warnings;
          if (!items.length) return <Typography variant="body2">-</Typography>;
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {items.slice(0, 2).map((item) => (
                <Chip key={item} size="small" color={blockers.length ? "warning" : "default"} variant="outlined" label={blockerLabel(item)} />
              ))}
              {items.length > 2 && <Chip size="small" label={`+${items.length - 2}`} />}
            </Stack>
          );
        },
      },
      {
        field: "ebay_offer_id",
        headerName: "Offer ID",
        minWidth: 130,
      },
      {
        field: "last_ebay_sync_at",
        headerName: "Last eBay sync",
        minWidth: 165,
      },
      {
        field: "last_price_push_at",
        headerName: "Last push",
        minWidth: 165,
      },
    ],
    []
  );

  const resultRows = Array.isArray(result?.rows) ? result.rows : [];

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between">
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Push Queue
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Approved repricing rows ready for controlled eBay price push.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" size="small" onClick={loadQueue} disabled={loading || pushing}>
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="warning"
            size="small"
            onClick={() => openConfirm("test")}
            disabled={loading || pushing || !testRow}
          >
            Push test row
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => openConfirm("selected")}
            disabled={loading || pushing || !selection.length}
          >
            Push selected
          </Button>
        </Stack>
      </Stack>

      {(loading || pushing) && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}

      <Alert severity="warning">
        This action mutates live eBay offer prices. Select only approved rows you intend to push now.
        Manual override warnings can be pushed after explicit confirmation, but technical blockers like missing IDs, stale sync, or not-listed status remain blocked.
        The app updates local price fields only after a post-push eBay refresh verifies the approved price.
      </Alert>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Chip label={`${numberText(summary?.row_count)} queue rows`} />
        <Chip color="success" label={`${numberText(summary?.eligible_count)} ready`} />
        <Chip color="warning" label={`${numberText(summary?.rejected_count)} blocked`} />
        <Chip color="error" label={`${numberText(selection.length)} selected`} />
        <Chip color={selectedEligibleCount === selection.length ? "success" : "warning"} label={`${numberText(selectedEligibleCount)} selected ready`} />
        {result?.summary ? <Chip color="success" label={`${numberText(result.summary.verified_count)} verified`} /> : null}
        {result?.summary?.mismatch_count ? <Chip color="error" label={`${numberText(result.summary.mismatch_count)} mismatched`} /> : null}
        {result?.summary?.verification_failed_count ? <Chip color="error" label={`${numberText(result.summary.verification_failed_count)} not verified`} /> : null}
      </Stack>

      <Paper variant="outlined" sx={{ height: 620, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.recommendation_id || row.id}
          loading={loading}
          checkboxSelection
          disableRowSelectionOnClick
          onRowSelectionModelChange={(model) => setSelection(idsFromModel(model))}
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
            sorting: { sortModel: [{ field: "push_eligible", sort: "desc" }] },
          }}
        />
      </Paper>

      {resultRows.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Push Results
          </Typography>
          <Stack spacing={1}>
            {resultRows.map((row) => (
              <Stack
                key={row.recommendation_id || row.id}
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip size="small" color={resultColor(row.push_status, row.push_success)} label={resultLabel(row)} />
                  <Typography variant="body2">
                    #{row.recommendation_id || row.id} {row.sku || ""} {money(row.pushed_price || row.approved_price)}
                  </Typography>
                  {row.verified_ebay_price !== undefined && row.verified_ebay_price !== null ? (
                    <Chip size="small" variant="outlined" label={`eBay ${money(row.verified_ebay_price)}`} />
                  ) : null}
                </Stack>
                {row.error || row.push_blockers?.length ? (
                  <Typography variant="body2" color="text.secondary">
                    {row.error || row.push_blockers.map(blockerLabel).join(", ")}
                  </Typography>
                ) : null}
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}

      <Dialog open={confirmOpen} onClose={() => (!pushing ? setConfirmOpen(false) : null)} fullWidth maxWidth="sm">
        <DialogTitle>Confirm eBay Price Push</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Alert severity="error">
              This will send approved prices to eBay for {numberText(confirmIds.length)} row(s). eBay prices can change immediately.
            </Alert>
            {confirmMode === "test" && testRow ? (
              <Alert severity="warning">
                Test row #{testRow.recommendation_id || testRow.id}: {testRow.sku || "no SKU"} to {money(testRow.approved_price)}.
              </Alert>
            ) : null}
            <TextField
              label={`Type ${CONFIRM_TEXT}`}
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value)}
              fullWidth
              size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={pushing}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={pushRows} disabled={!confirmationReady || pushing}>
            Push to eBay
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
